import React from 'react';
import {motion} from 'framer-motion';
import {useTranslation} from 'react-i18next';
import {FaTools, FaIndustry, FaLightbulb, FaChartLine, FaShieldAlt, FaArrowRight} from 'react-icons/fa';
import {Link} from 'react-router-dom';
import Seo from '../../../shared/seo/Seo';

import PageHeader from 'features/common/PageHeader';
import createServiceData from '../data/serviceData';
import {ServiceData} from '../types';
import { getCanonicalUrl, pageMeta } from 'shared/seo/seoMeta';
import './ServicesPage.css';

const ServicesPage: React.FC = () => {
    const {t} = useTranslation();

    const serviceData: ServiceData = createServiceData(t);

    // Additional services for the bottom section
    const additionalServices = [
        {
            icon: <FaIndustry/>,
            title: t('services.additionalServices.industrial.title', 'Automatisation Industrielle'),
            description: t('services.additionalServices.industrial.description', 'Solutions d\'automatisation pour optimiser vos processus industriels et augmenter votre productivité.')
        },
        {
            icon: <FaLightbulb/>,
            title: t('services.additionalServices.energy.title', 'Installations Électriques'),
            description: t('services.additionalServices.energy.description', 'Services d\'installation et de maintenance électrique pour tous types d\'infrastructures industrielles.')
        },
        {
            icon: <FaChartLine/>,
            title: t('services.additionalServices.optimization.title', 'Conseil en Efficacité'),
            description: t('services.additionalServices.optimization.description', 'Expertise en optimisation des processus industriels pour réduire les coûts et améliorer les performances.')
        },
        {
            icon: <FaShieldAlt/>,
            title: t('services.additionalServices.compliance.title', 'Sécurité Industrielle'),
            description: t('services.additionalServices.compliance.description', 'Solutions pour assurer la sécurité de vos installations et de votre personnel.')
        }
    ];

    return (
        <>
            <Seo
              title={pageMeta.services.title}
              description={pageMeta.services.description}
              canonical={getCanonicalUrl(pageMeta.services.canonicalPath)}
            />
            <PageHeader
                title={t('services.title', 'os Services')}

                subtitle={t('services.subtitle', 'Solutions industrielles complètes adaptées à vos besoins')}
                background="/images/background/services-bg.webp"
            />

            <section className="services-section">
                <div className="container">
                    <div className="section-header">
                        <motion.h2
                            initial={{opacity: 0, y: 20}}
                            whileInView={{opacity: 1, y: 0}}
                            transition={{duration: 0.6}}
                            viewport={{once: true}}
                        >
                            {t('services.mainTitle')}
                        </motion.h2>
                        <motion.p
                            initial={{opacity: 0, y: 20}}
                            whileInView={{opacity: 1, y: 0}}
                            transition={{duration: 0.6, delay: 0.2}}
                            viewport={{once: true}}
                        >
                            {t('services.mainDescription',)}
                        </motion.p>
                    </div>

                    <div className="services-grid">
                        {Object.values(serviceData).map((service, index) => (
                            <motion.div
                                key={service.id}
                                className="service-card"
                                initial={{opacity: 0, y: 30}}
                                whileInView={{opacity: 1, y: 0}}
                                transition={{duration: 0.6, delay: index * 0.1}}
                                viewport={{once: true}}
                            >
                                <img
                                    className="service-image"
                                    src={service.image}
                                    alt={service.title}
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = '/images/services/placeholder-service.jpg';
                                    }}
                                />
                                <div className="service-content">
                                    <div className="service-icon">{service.icon}</div>
                                    <Link
                                        to={`/services/${service.slug}`}
                                        className="service-title-link"
                                        onClick={() => window.scrollTo(0, 0)}
                                    >
                                        <h3>{service.title}</h3>
                                    </Link>
                                    <div dangerouslySetInnerHTML={{__html: service.description}}/>

                                    <div className="service-features">
                                        {service.features && service.features.map((feature, idx) => (
                                            <div key={idx} className="feature-item-services">
                                                <span className="feature-dot"></span>
                                                <span>{feature.title}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <Link to={`/services/${service.slug}`} className="service-details-button"
                                          onClick={() => window.scrollTo(0, 0)}>
                                        <span>{t('services.viewDetails', 'Voir les détails')}</span>
                                        <span className="arrow"><FaArrowRight/></span>
                                    </Link>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="additional-services-section">
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{opacity: 0, y: 30}}
                        whileInView={{opacity: 1, y: 0}}
                        transition={{duration: 0.6}}
                        viewport={{once: true}}
                    >
                        <h2 className="section-title">{t('services.additionalServices.title', 'Services Complémentaires')}</h2>
                        <p className="section-subtitle">
                            {t('services.additionalServices.subtitle', 'Nous proposons également d\'autres services pour répondre à tous vos besoins industriels')}
                        </p>
                    </motion.div>

                    <div className="additional-services-grid">
                        {additionalServices.map((service, index) => (
                            <motion.div
                                key={index}
                                className="additional-service-card"
                                initial={{opacity: 0, y: 30}}
                                whileInView={{opacity: 1, y: 0}}
                                transition={{duration: 0.6, delay: index * 0.1}}
                                viewport={{once: true}}
                            >
                                <div className="additional-service-icon">{service.icon}</div>
                                <h3>{service.title}</h3>
                                <p>{service.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="cta-section-service">
                <div className="container">
                    <motion.div
                        className="cta-container-service"
                        initial={{opacity: 0, y: 30}}
                        whileInView={{opacity: 1, y: 0}}
                        transition={{duration: 0.6}}
                        viewport={{once: true}}
                    >
                        <div className="cta-content-service">
                            <h2 className="cta-title-service">{t('services.cta.title', 'Besoin d\'un service sur mesure?')}</h2>
                            <p className="cta-description-service">
                                {t('services.cta.description', 'Contactez-nous pour discuter de vos besoins spécifiques et obtenir une solution adaptée à votre situation.')}
                            </p>
                            <Link to="/contact" className="cta-button-service" onClick={() => window.scrollTo(0, 0)}>
                                {t('services.cta.button', 'Nous contacter')}
                                <svg width="18" height="12" viewBox="0 0 18 12" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 1L17 6M17 6L12 11M17 6H1" stroke="currentColor" strokeWidth="2"
                                          strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            <section className="process-section">
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{opacity: 0, y: 30}}
                        whileInView={{opacity: 1, y: 0}}
                        transition={{duration: 0.6}}
                        viewport={{once: true}}
                    >
                        <h2 className="section-title">{t('services.process.title', 'Notre Processus')}</h2>
                        <p className="section-subtitle">
                            {t('services.process.subtitle', 'Comment nous travaillons pour vous offrir un service de qualité')}
                        </p>
                    </motion.div>

                    <div className="process-steps">
                        {[1, 2, 3, 4].map((step, index) => (
                            <motion.div
                                key={index}
                                className="process-step"
                                initial={{opacity: 0, y: 30}}
                                whileInView={{opacity: 1, y: 0}}
                                transition={{duration: 0.6, delay: index * 0.1}}
                                viewport={{once: true}}
                            >
                                <div className="process-step-number">0{step}</div>
                                <div className="process-step-icon">
                                    {index === 0 && <FaChartLine/>}
                                    {index === 1 && <FaTools/>}
                                    {index === 2 && <FaIndustry/>}
                                    {index === 3 && <FaLightbulb/>}
                                </div>
                                <h3>{t(`services.process.steps.${index + 1}.title`, `Step ${step}`)}</h3>
                                <p>
                                    {t(`services.process.steps.${index + 1}.description`, `Description for step ${step}`)}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

        </>
    );
};

export default ServicesPage;
